import { cellSec } from "../clock";
import { Emitter, seqColumns } from "../emitters";
import { AppContext } from "../appContext";

interface SequencerPanelDeps {
  editEmitterSeqSteps(em: Emitter, token: string, nextSteps: number[]): void;
  uiNoSeq(): boolean;
}

export interface SequencerPanel {
  rebuild(): void;
  updatePlayhead(): void;
  setPlayModeHidden(hidden: boolean): void;
}

/**
 * The bottom step-sequencer panel: one lane per clock-synced seq-mode emitter
 * (emitIn-driven lanes are hidden), cells toggling steps through the undoable
 * edit path, and a per-frame playhead highlight.
 */
export function initSequencerPanel(ctx: AppContext, deps: SequencerPanelDeps): SequencerPanel {
  const { particles, emitterField, graph } = ctx;

  function toggleSeqStep(em: Emitter, cellIndex: number): void {
    const next = em.seqSteps.includes(cellIndex)
      ? em.seqSteps.filter((s: number): boolean => s !== cellIndex)
      : [...em.seqSteps, cellIndex].sort((a: number, b: number): number => a - b);
    deps.editEmitterSeqSteps(em, "emSeqStep", next);
  }

  const root = document.createElement("div");
  root.className = "seq-panel";
  const lanesEl = document.createElement("div");
  lanesEl.className = "seq-lanes";
  root.append(lanesEl);
  document.body.appendChild(root);

  let playModeHidden = false;
  let laneCells: HTMLButtonElement[][] = [];
  let laneRows: HTMLDivElement[][] = [];
  let laneNums: number[] = [];
  let laneCols: number[] = [];
  let lastSteps: number[] = [];
  let lastPlayRows: number[] = [];

  function rebuild(): void {
    const list = emitterField.list();
    const selected = emitterField.selectedEmitter();
    lanesEl.replaceChildren();
    laneCells = [];
    laneRows = [];
    laneNums = [];
    laneCols = [];
    lastSteps = [];
    lastPlayRows = [];
    if (deps.uiNoSeq()) {
      root.style.display = "none";
      return;
    }
    const lanes = list
      .map((em: Emitter, index: number): { em: Emitter; index: number } => ({ em, index }))
      .filter(
        (l: { em: Emitter; index: number }): boolean =>
          l.em.clockSync &&
          l.em.seqMode &&
          graph.edgesTo(l.em.id, "emitIn").length === 0,
      );
    if (lanes.length === 0) {
      root.style.display = "none";
      return;
    }
    root.style.display = playModeHidden ? "none" : "";
    for (const { em, index } of lanes) {
      const activeCells = new Set(em.seqSteps);
      const cols = seqColumns(em.meter);
      const laneEl = document.createElement("div");
      laneEl.className = em === selected ? "seq-lane selected" : "seq-lane";
      const label = document.createElement("span");
      label.className = "seq-lane-label";
      label.textContent = em.name || `E${index + 1}`;
      const rowsEl = document.createElement("div");
      rowsEl.className = "seq-rows";
      const cells: HTMLButtonElement[] = [];
      const rowEls: HTMLDivElement[] = [];
      const rowCount = em.num / cols;
      for (let r = 0; r < rowCount; r++) {
        const rowEl = document.createElement("div");
        rowEl.className = "seq-cells";
        if (rowCount >= 3 && r >= 2) {
          rowEl.style.display = "none";
        }
        rowsEl.appendChild(rowEl);
        if (rowCount >= 3) {
          const rowNum = document.createElement("span");
          rowNum.className = "seq-row-num";
          rowNum.textContent = String(r + 1);
          rowEl.appendChild(rowNum);
        }
        for (let c = 0; c < cols; c++) {
          const s = r * cols + c;
          const cell = document.createElement("button");
          cell.className = activeCells.has(s) ? "seq-cell active" : "seq-cell";
          if (c % 2 === 0) {
            cell.classList.add("beat-head");
          }
          cell.addEventListener("click", (): void => toggleSeqStep(em, s));
          rowEl.appendChild(cell);
          cells.push(cell);
        }
        rowEls.push(rowEl);
      }
      laneEl.append(label, rowsEl);
      lanesEl.appendChild(laneEl);
      laneCells.push(cells);
      laneRows.push(rowEls);
      laneNums.push(em.num);
      laneCols.push(cols);
      lastSteps.push(-1);
      lastPlayRows.push(-1);
    }
  }

  /**
   * Lanes of three or more bars render as a rolling two-row window: the
   * playing bar keeps its top/bottom slot (odd bars top, even bars bottom)
   * and the other slot previews the next bar, so while bar 2 plays the top
   * slot already shows bar 3. Slots are realized with flex `order`, which
   * lets the next bar render above the playing one regardless of DOM order.
   */
  function updatePlayhead(): void {
    if (root.style.display === "none" || laneCells.length === 0) {
      return;
    }
    const cell = cellSec(particles.clockPeriodSeconds());
    const globalCell = Math.floor(particles.clockTimeSec() / cell);
    for (let i = 0; i < laneCells.length; i++) {
      const step = globalCell % laneNums[i];
      if (step === lastSteps[i]) {
        continue;
      }
      const rows = laneRows[i];
      if (rows.length >= 3) {
        const playRow = Math.floor(step / laneCols[i]);
        if (playRow !== lastPlayRows[i]) {
          const nextRow = (playRow + 1) % rows.length;
          rows.forEach((rowEl: HTMLDivElement, r: number): void => {
            rowEl.style.display = r === playRow || r === nextRow ? "" : "none";
          });
          rows[playRow].style.order = String(playRow % 2);
          rows[nextRow].style.order = String(1 - (playRow % 2));
          lastPlayRows[i] = playRow;
        }
      }
      laneCells[i].forEach((cellEl: HTMLButtonElement, s: number): void => {
        cellEl.classList.toggle("playhead", s === step);
      });
      lastSteps[i] = step;
    }
  }

  /**
   * Hides / restores the sequencer panel UI for play mode. UI-only: the emitter
   * sequencing keeps running while hidden; only the lane display is suppressed.
   */
  function setPlayModeHidden(hidden: boolean): void {
    playModeHidden = hidden;
    if (hidden) {
      root.style.display = "none";
    } else {
      rebuild();
    }
  }

  return { rebuild, updatePlayhead, setPlayModeHidden };
}
